/**
 * Response parsing: normalize HTTP responses into one of three shapes.
 * Port of fastSDK's `ResponseParser`, Socaity/APIPod only:
 *
 * 1. `StreamingResponse` — live SSE body (kept un-consumed for a StreamSession)
 * 2. `SocaityJobResponse` — job envelope (drives the polling loop)
 * 3. direct value — plain JSON, text, or a typed media file
 */

import { MediaFileFactory, isFileResult } from '@socaity/media-toolkit';

import { SocaityJobResponse, StreamingResponse, isJobResponse } from '../schemas/transport';
import { bytesToResult } from '../streaming/sse';

/** Parse a fetch Response into a job envelope, stream sentinel, or direct value. */
export async function parseResponse(
  response: Response,
): Promise<StreamingResponse | SocaityJobResponse | any> {
  const contentType = response.headers.get('content-type') ?? '';

  if (contentType.includes('text/event-stream')) {
    return new StreamingResponse(response);
  }

  if (contentType.includes('application/json')) {
    const data = await response.json();
    return isJobResponse(data) ? normalizeJobResponse(data) : data;
  }

  const bytes = new Uint8Array(await response.arrayBuffer());
  return bytesToResult(bytes);
}

/** Coerce legacy wire quirks (dict-shaped progress) into the schema shape. */
function normalizeJobResponse(data: Record<string, any>): SocaityJobResponse {
  const normalized: SocaityJobResponse = { ...data } as SocaityJobResponse;
  if (data.progress && typeof data.progress === 'object') {
    normalized.message = normalized.message ?? data.progress.message ?? null;
    const value = Number(data.progress.progress);
    normalized.progress = Number.isFinite(value) ? value : null;
  }
  return normalized;
}

/** Recursively convert FileModel payloads inside a result into typed media files. */
export async function parseMediaResult(result: any): Promise<any> {
  if (result === null || result === undefined) return result;

  if (Array.isArray(result)) {
    return Promise.all(result.map(parseMediaResult));
  }

  if (isFileResult(result)) {
    return mediaFromFileModel(result);
  }

  if (typeof result === 'object' && result.constructor === Object) {
    const entries = await Promise.all(
      Object.entries(result).map(async ([key, value]) => [key, await parseMediaResult(value)]),
    );
    return Object.fromEntries(entries);
  }

  return result;
}

/**
 * Build a media file from a FileModel payload. Content is normally base64 or
 * a URL; queue-backed byte streams can deliver raw bytes as a latin-1 JSON
 * string instead — recover those byte-wise and detect the type from magic bytes.
 */
async function mediaFromFileModel(fileModel: Record<string, any>): Promise<any> {
  try {
    const media = await MediaFileFactory.create(fileModel);
    if (media.fileSize() > 0) return media;
  } catch {
    // fall through to raw-string recovery
  }
  const content = fileModel.content;
  if (typeof content === 'string' && content.length > 0) {
    const bytes = Uint8Array.from(content, (char) => char.charCodeAt(0) & 0xff);
    return bytesToResult(bytes);
  }
  return fileModel;
}
